import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { COPY } from "../shared/copy";
import { getLeagueState, getLeaderboardWithMovement, getSeasonWithWeeks, type Member } from "../lib/league";
import { describeWeek, DEFAULT_WEEK_WIN_BONUS, type WeekResult } from "../shared/weekly";
import { Avatar } from "../components/Avatar";
import { rankBadge } from "../shared/format";
import { Movement } from "./Home";

// Full leaderboard. Ranked by points; ties ordered by average correct response
// time, then total. Speed is NEVER points. A small avg is shown only to explain a
// tie. If a league stake is configured, show an informational note only.
export function Leaderboard() {
  const { leagueId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState<any[] | null>(null);
  const [weeks, setWeeks] = useState<WeekResult[]>([]);
  const [bonus, setBonus] = useState(0);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  // Two views of the same season. The season board says who is winning; the week
  // says who is winning RIGHT NOW, which is the one that keeps somebody playing
  // after a bad start.
  const [view, setView] = useState<"season" | "week">("season");
  const [failed, setFailed] = useState(false);

  async function load() {
    setFailed(false);
    try {
      const { season, members } = await getLeagueState(leagueId!, user!.id);
      setNames(new Map((members as Member[]).map((m) => [m.user_id, m.display_name])));
      if (!season) { setBoard([]); setWeeks([]); return; }

      // Movement comes from the flat board; the week bonuses come from the
      // weekly one. Merged so a row can show both where somebody stands and how
      // they got there.
      const [movement, seasonal] = await Promise.all([
        getLeaderboardWithMovement(season.id, members as Member[]),
        getSeasonWithWeeks(season, members as Member[]),
      ]);
      const moveOf = new Map(movement.map((m: any) => [m.user_id, m.movement]));
      setBoard(seasonal.board.map((e) => ({ ...e, movement: moveOf.get(e.user_id) ?? null })));
      setWeeks(seasonal.weeks);
      setBonus(season.week_win_bonus ?? DEFAULT_WEEK_WIN_BONUS);
    } catch {
      setFailed(true);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [leagueId, user]);

  // The most recent week is "this week".
  const thisWeek = weeks.length > 0 ? weeks[weeks.length - 1] : undefined;

  // Which rows the chosen view shows. The week view ranks that week alone, with
  // names filled in from the roster.
  const rows = view === "week"
    ? (thisWeek?.standings ?? []).map((e) => ({
        ...e, name: names.get(e.user_id) ?? "Friend", movement: null, bonus_points: 0, weeks_won: 0,
      }))
    : board;

  // Show the avg-time hint only on rows that tie another row on points.
  const pointCounts = new Map<number, number>();
  for (const e of rows ?? []) pointCounts.set(e.points, (pointCounts.get(e.points) ?? 0) + 1);
  // Nobody has played a single question yet, so this is a roster, not a ranking.
  const noResultsYet = !!rows && rows.length > 0 && rows.every((e: any) => (e.attempted_count ?? 0) === 0);

  if (failed) {
    return (
      <div className="app">
        <div className="header">
          <h1>Leaderboard</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
        </div>
        <div className="card">
          <p className="error">We couldn&apos;t load the standings.</p>
          <p className="muted">That is usually the connection. Try again.</p>
          <button className="btn primary" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="header">
        <h1>Leaderboard</h1>
        <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
      </div>
      {weeks.length > 0 && (
        <div className="seg" role="tablist" aria-label="Which standings">
          {([["season", "Season"], ["week", "This week"]] as const).map(([k, label]) => (
            <button
              key={k}
              role="tab"
              aria-selected={view === k}
              className={`seg-btn${view === k ? " current" : ""}`}
              onClick={() => setView(k)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <p className="muted">
        {COPY.leaderboard.scoring}
        {view === "season" && bonus > 0 && ` Winning a week is worth ${bonus} more.`}
        {view === "season" && board !== null && board.some((e) => e.movement != null && e.movement !== 0) && " Arrows show how far you moved since the last drop."}
      </p>

      {view === "week" && thisWeek && (
        <div className="card live">
          <span className="tag">Week {thisWeek.week}</span>
          <p>{describeWeek(thisWeek, (id) => names.get(id) ?? "Friend")}</p>
          <p className="muted">
            {thisWeek.settled
              ? "This week is done. A new one starts with the next drop."
              : "Still going. Win the week and you pick up the bonus, however the season is looking."}
          </p>
        </div>
      )}

      {board === null && <div className="loading">Loading the standings…</div>}

      {noResultsYet && (
        <div className="card live">
          <span className="tag">Nothing to rank yet</span>
          <p>No drop has finished, so everybody is sitting on zero. Here&apos;s who&apos;s in.</p>
        </div>
      )}

      {rows !== null && rows.length > 0 && (
      <div className="card">
        {rows.map((e: any) => {
          const tied = (pointCounts.get(e.points) ?? 0) > 1 && e.avg_correct_response_ms != null;
          return (
            <div className={`lb-row ${e.user_id === user!.id ? "me" : ""}`} key={e.user_id}>
              <span className="lb-rank">{rankBadge(e.rank, e.points)}</span>
              <Avatar id={e.user_id} name={e.name} />
              <span className="lb-name">
                {e.name}{e.user_id === user!.id ? " (you)" : ""}
                {tied && <span className="muted"> · {(e.avg_correct_response_ms / 1000).toFixed(1)}s avg</span>}
                {/* Same rule as the home preview: somebody who has not played a
                    question yet gets no record line rather than "0/0 correct". */}
                {e.attempted_count > 0 && (
                  <>
                    <br />
                    <span className="muted lb-record">{e.correct_count}/{e.attempted_count} correct</span>
                  </>
                )}
              </span>
              <Movement value={e.movement} />
              <span className="lb-points">
                {e.points}
                {/* An unexplained jump in somebody's total is the fastest way to
                    make a game feel rigged, so the bonus says where it came from. */}
                {view === "season" && e.bonus_points > 0 && (
                  <span className="lb-bonus">
                    +{e.bonus_points}
                    <span className="lb-bonus-why">{e.weeks_won}w</span>
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}
